import React, { useEffect, useState, useMemo } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { BottomTabs } from "@/components/BottomTabs";
import { api, API } from "@/lib/api";
import dayjs from "dayjs";
import { LayoutDashboard, Users, FolderKanban, FileDown, Megaphone, Plus, History, AlertTriangle, X, ClipboardCheck, CheckCircle2, Clock, AlertCircle, PackageCheck, ChevronDown, ChevronUp, ShieldCheck, Mail, BarChart3, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { formatRange, mondayOf, ROLE_LABEL, todayPT, currentYearPT } from "@/lib/util";
import LoadError from "@/components/LoadError";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import SafetyMeetings from "@/pages/safety/SafetyMeetings";
import SafetyMeetingReport from "@/pages/safety/SafetyMeetingReport";
import SafetyComplianceRollup from "@/pages/safety/SafetyComplianceRollup";
import HoursByProjectReport from "@/pages/HoursByProjectReport";

function Card({ children, className = "", testId }) {
    return <div data-testid={testId} className={`bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5 ${className}`}>{children}</div>;
}

// "Was last week heavier than this one." Direction is carried by an arrow and a
// word, never by colour alone -- more hours is not good or bad on its own, and
// red/green would assert a judgement the data does not support. Overtime used
// to be broken out here as the number that costs money; Talon records it
// outside this app, so the server no longer sends it and the line is gone.
function WeekComparison({ current, prior, change }) {
    if (!current || !prior || !change) return null;

    const Line = ({ label, now, then, delta, unit = "h" }) => {
        const up = delta > 0;
        const flat = delta === 0;
        return (
            <div className="flex items-baseline justify-between gap-3 py-1.5" data-testid={`compare-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="text-sm text-brand-charcoal/75">{label}</span>
                <span className="flex items-baseline gap-2 tabular-nums">
                    <span className="font-bold text-brand-charcoal">{now.toFixed(unit === "h" ? 2 : 0)}{unit}</span>
                    <span className={`text-xs font-semibold ${flat ? "text-brand-steel" : "text-brand-charcoal"}`}>
                        {flat ? "same as" : `${up ? "▲" : "▼"} ${Math.abs(delta).toFixed(unit === "h" ? 2 : 0)}${unit} vs`}
                        {" "}
                        <span className="text-brand-steel">{then.toFixed(unit === "h" ? 2 : 0)}{unit}</span>
                    </span>
                </span>
            </div>
        );
    };

    const pct = change.percent;
    return (
        <Card testId="week-comparison">
            <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="text-[10px] uppercase tracking-widest text-brand-navy">Compared with the week before</div>
                <div className="text-[10px] text-brand-steel">{formatRange(prior.week_start)}</div>
            </div>
            {/* The headline answer, in words, before any numbers. */}
            <div className="font-bold text-lg text-brand-charcoal mb-1" data-testid="compare-headline">
                {change.worked === 0
                    ? "Same hours as the week before."
                    : `${change.worked > 0 ? "Heavier" : "Lighter"} than the week before${pct === null ? "" : ` — ${Math.abs(pct)}%`}.`}
            </div>
            <div className="divide-y divide-brand-charcoal/10">
                <Line label="Hours worked" now={current.worked} then={prior.worked} delta={change.worked} />
                <Line label="People working" now={current.people} then={prior.people} delta={change.people} unit="" />
            </div>
            {prior.worked === 0 && (
                <p className="text-xs text-brand-steel mt-2" data-testid="compare-no-baseline">
                    No hours were recorded the week before, so there is nothing to compare against.
                </p>
            )}
        </Card>
    );
}

/** Seed and reset controls for the practice environment.
 *
 *  Rendered only where REACT_APP_PRACTICE_MODE is "true", which production does
 *  not set. Both endpoints are admin-authenticated on the server regardless --
 *  this gate hides the buttons, it is not what protects them.
 *
 *  Reset deletes the practice crew's work for the current week. That is exactly
 *  what it is for in a sandbox and exactly what nobody wants to discover by
 *  accident, so it confirms first. */
function PracticeTools() {
    const [busy, setBusy] = useState(null);
    const [msg, setMsg] = useState(null);

    if (process.env.REACT_APP_PRACTICE_MODE !== "true") return null;

    async function run(kind) {
        if (kind === "reset" && !window.confirm(
            "Clear this week's practice timecards, entries and acknowledgements " +
            "for the practice crew?\n\nThe people, jobs and assignments stay.")) return;
        setBusy(kind); setMsg(null);
        try {
            const r = await api.post(kind === "seed" ? "/seed/demo" : "/seed/reset-demo-week");
            setMsg(kind === "seed"
                ? `Seeded ${r.data.employees?.length ?? 0} accounts and ${r.data.projects?.length ?? 0} jobs.`
                : `Cleared ${r.data.deleted_timecards ?? 0} timecards and ${r.data.deleted_entries ?? 0} entries.`);
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Failed. Check the API logs.");
        } finally { setBusy(null); }
    }

    return (
        <Card testId="practice-tools">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-bold text-sm text-brand-charcoal">Practice data</div>
                    <div className="text-xs text-brand-charcoal/60">
                        Seeding is safe to repeat. Reset clears this week's work and leaves the crew and jobs alone.
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => run("seed")} disabled={!!busy}
                        className="h-10 px-4 rounded-xl bg-brand-navy text-brand-bone font-bold text-xs uppercase tracking-widest disabled:opacity-40"
                        data-testid="practice-seed">
                        {busy === "seed" ? "Seeding…" : "Seed practice data"}
                    </button>
                    <button onClick={() => run("reset")} disabled={!!busy}
                        className="h-10 px-4 rounded-xl bg-brand-gold hover:bg-brand-gold/80 text-brand-charcoal font-bold text-xs uppercase tracking-widest disabled:opacity-40"
                        data-testid="practice-reset">
                        {busy === "reset" ? "Resetting…" : "Reset practice data"}
                    </button>
                </div>
            </div>
            {msg && <div className="text-xs text-brand-navy font-semibold mt-2" data-testid="practice-result">{msg}</div>}
        </Card>
    );
}


function Dashboard() {
    const [d, setD] = useState(null);
    const [week, setWeek] = useState(mondayOf(todayPT()));

    const [err, setErr] = useState(null);

    const loadDashboard = async (w) => {
        setErr(null);
        try {
            const r = await api.get(`/dashboard/admin?week=${w}`);
            setD(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load the dashboard.");
        }
    };
    useEffect(() => { loadDashboard(week); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps

    if (err && !d) return <LoadError message={err} onRetry={() => loadDashboard(week)} testId="admin-dashboard-error" />;
    if (!d) return <div className="p-6 text-brand-charcoal/60">Loading…</div>;
    return (
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="admin-dashboard">
            <div className="bg-brand-admin-header text-brand-bone rounded-2xl p-5 shadow-lift flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Pay Period {d.pay_period} of 52 · {d.year}</div>
                    <div className="font-bold text-3xl mt-1">{formatRange(d.week_start)}</div>
                </div>
                {/* This always was a live week picker driving the whole screen, but
                    it read as a static date stamp. Labelling it says it is a control. */}
                <label className="shrink-0 text-right">
                    <span className="block text-[10px] uppercase tracking-widest text-brand-gold mb-1">Show week of</span>
                    <input type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))} className="text-xs text-brand-charcoal rounded-lg px-2 py-1.5 border border-brand-bone/40" data-testid="dashboard-week-picker" />
                </label>
            </div>
            <PracticeTools />
            <WeekComparison current={d.this_week} prior={d.prior_week} change={d.change} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card testId="tile-total"><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Timecards</div><div className="font-bold text-4xl mt-1">{d.total_timecards}</div></Card>
                <Card testId="tile-approved"><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Approved · Export-ready</div><div className="font-bold text-4xl mt-1 text-brand-success">{d.approved_ready}</div></Card>
                <Card testId="tile-employees"><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Hours · employees</div><div className="font-serif font-bold text-2xl mt-1">{d.hours_by_employee.length}</div></Card>
                {/* Was "Sick · unpaid" -- attendance exceptions the office used
                    to chase here. Neither is recorded in this version, so the
                    tile could only ever read 0.00 / 0.00. The week's hours are
                    the figure the rest of this screen is a breakdown of, and
                    nothing above stated the whole. */}
                <Card testId="tile-hours"><div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Hours this week</div><div className="font-serif font-bold text-2xl mt-1 tabular-nums">{(d.this_week?.worked ?? 0).toFixed(2)}</div></Card>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <Card testId="hours-by-employee">
                    <div className="text-[10px] uppercase tracking-widest text-brand-navy mb-2">Hours by employee</div>
                    <ul className="text-sm divide-y divide-brand-charcoal/10">
                        {d.hours_by_employee.length === 0 && <li className="py-3 text-brand-charcoal/60">No hours yet.</li>}
                        {d.hours_by_employee.map((e) => (
                            <li key={e.employee_id} className="flex justify-between py-2">
                                <span>{e.name}</span><span className="font-bold">{e.hours.toFixed(2)}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
                <Card testId="hours-by-project">
                    <div className="text-[10px] uppercase tracking-widest text-brand-navy mb-2">Hours by project</div>
                    <ul className="text-sm divide-y divide-brand-charcoal/10">
                        {d.hours_by_project.length === 0 && <li className="py-3 text-brand-charcoal/60">No project hours yet.</li>}
                        {d.hours_by_project.map((p) => (
                            <li key={p.project_id} className="flex justify-between py-2">
                                <span>{p.name}</span><span className="font-bold">{p.hours.toFixed(2)}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            </div>
        </div>
    );
}

function Employees() {
    const [rows, setRows] = useState([]);
    const [projects, setProjects] = useState([]);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ email: "", name: "", employee_code: "", role: "worker" });
    const [assignFor, setAssignFor] = useState(null);
    const [assigns, setAssigns] = useState({});
    const [resending, setResending] = useState(null);
    const [resendMsg, setResendMsg] = useState({});

    const [err, setErr] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const load = async () => {
        setErr(null);
        try {
            const [e, p] = await Promise.all([api.get("/employees"), api.get("/projects")]);
            setRows(e.data);
            setProjects(p.data);
            setLoaded(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load people.");
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0)),
        [rows]
    );

    const loadAssigns = async (empId) => {
        const r = await api.get(`/employees/${empId}/assignments`);
        setAssigns((a) => ({ ...a, [empId]: r.data.map((x) => x.project_id) }));
    };

    const submitNew = async () => {
        try {
            await api.post("/employees", draft);
            setDraft({ email: "", name: "", employee_code: "", role: "worker" });
            setAdding(false);
            await load();
        } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    };

    const toggleActive = async (e) => {
        await api.patch(`/employees/${e.employee_id}`, { active: !e.active });
        await load();
    };

    const setRole = async (e, role) => {
        await api.patch(`/employees/${e.employee_id}`, { role });
        await load();
    };

    const toggleAssignment = async (empId, projId, isOn) => {
        if (isOn) await api.delete("/employees/assign", { data: { employee_id: empId, project_id: projId } });
        else await api.post("/employees/assign", { employee_id: empId, project_id: projId });
        await loadAssigns(empId);
    };

    const resendWelcome = async (emp) => {
        setResending(emp.employee_id);
        setResendMsg((m) => ({ ...m, [emp.employee_id]: null }));
        try {
            const r = await api.post(`/employees/${emp.employee_id}/resend-welcome`);
            const sent = r.data.status === "sent";
            setResendMsg((m) => ({ ...m, [emp.employee_id]: sent ? "Sent ✓" : "Link logged (no email key)" }));
        } catch (e) {
            setResendMsg((m) => ({ ...m, [emp.employee_id]: e?.response?.data?.detail || "Failed" }));
        } finally {
            setResending(null);
        }
    };

    const deleteEmployee = async (emp) => {
        if (!window.confirm(
            `⚠️ PERMANENTLY DELETE "${emp.name}"?\n\nThis will delete ALL their timecards, time entries, and records. This CANNOT be undone.\n\nIf they are just taking a break, use "Pause" instead.\n\nOK to confirm permanent deletion.`
        )) return;
        try {
            await api.delete(`/employees/${emp.employee_id}`);
            await load();
        } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
    };

    if (err && !loaded) return <LoadError message={err} onRetry={load} testId="admin-employees-error" />;

    return (
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="admin-employees">
            <div className="flex justify-between items-center">
                <h2 className="font-bold text-2xl">Employees</h2>
                <button onClick={() => setAdding(true)} className="h-10 px-3 rounded-xl bg-brand-navy text-brand-bone font-bold flex items-center gap-2" data-testid="add-employee-button"><Plus className="h-4 w-4" />Add employee</button>
            </div>

            {adding && (
                <Card testId="add-employee-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <input placeholder="Email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-emp-email" />
                        <input placeholder="Full name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-emp-name" />
                        <input placeholder="Employee code" value={draft.employee_code} onChange={(e) => setDraft({ ...draft, employee_code: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-emp-code" />
                        <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-emp-role">
                            <option value="worker">CREW (worker)</option>
                            <option value="manager">FOREMAN (manager)</option>
                            <option value="admin">ADMIN (admin)</option>
                        </select>
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button onClick={submitNew} className="h-11 px-4 rounded-xl bg-brand-navy text-brand-bone font-bold" data-testid="save-new-emp">Save</button>
                        <button onClick={() => setAdding(false)} className="h-11 px-4 rounded-xl bg-brand-stone">Cancel</button>
                    </div>
                </Card>
            )}

            <Card testId="employees-list">
                <ul className="divide-y divide-brand-charcoal/10">
                    {sortedRows.map((e) => (
                        <li key={e.employee_id} className={`py-3 ${!e.active ? "opacity-60" : ""}`} data-testid={`emp-${e.employee_id}`}>
                            <div className="flex justify-between items-start gap-3">
                                <div className="min-w-0">
                                    <div className="font-bold text-brand-charcoal">
                                        {e.name} <span className="text-brand-charcoal/50 text-xs">{e.employee_code}</span>
                                        {!e.active && <span className="ml-2 text-[10px] uppercase tracking-widest bg-brand-warn/15 text-brand-charcoal px-1.5 py-0.5 rounded">Paused</span>}
                                    </div>
                                    <div className="text-xs text-brand-charcoal/60">{e.email}</div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                    <select value={e.role} onChange={(ev) => setRole(e, ev.target.value)} className="text-xs border border-brand-charcoal/20 rounded-lg px-2 py-1" data-testid={`role-select-${e.employee_id}`}>
                                        <option value="worker">CREW</option>
                                        <option value="manager">FOREMAN</option>
                                        <option value="admin">ADMIN</option>
                                    </select>
                                    {/* This was a green "Active" pill on every row -- decorating
                                        the normal state, and easy to read as a label rather than
                                        the control it is. Active is the default and needs no
                                        badge; the exception already shows as "Paused" beside the
                                        name. What remains is the action itself, named for what
                                        it does. */}
                                    <button onClick={() => toggleActive(e)} className={`text-xs px-2 py-1 rounded-lg font-semibold ${e.active ? "text-brand-charcoal/60 hover:bg-brand-stone" : "bg-brand-gold/30 text-brand-charcoal hover:bg-brand-gold/50"}`} data-testid={`toggle-active-${e.employee_id}`} title={e.active ? "Stop this person appearing on timecards and crew lists" : "Put this person back on crew lists"}>
                                        {e.active ? "Pause" : "Reactivate"}
                                    </button>
                                    {/* "Resend" alone never said resend WHAT. */}
                                    <button onClick={() => resendWelcome(e)} disabled={resending === e.employee_id} className="text-xs px-2 py-1 rounded-lg bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold flex items-center gap-0.5 disabled:opacity-50" data-testid={`resend-welcome-${e.employee_id}`} title="Email this person a fresh link to set their password and sign in">
                                        <Mail className="h-3 w-3" />{resending === e.employee_id ? "Sending…" : "Resend invite"}
                                    </button>
                                    <button onClick={() => deleteEmployee(e)} className="text-xs px-2 py-1 rounded-lg bg-brand-danger/10 hover:bg-brand-danger/10 text-brand-danger font-bold flex items-center gap-0.5" data-testid={`delete-emp-${e.employee_id}`} title="Permanently delete employee and all records">
                                        <X className="h-3 w-3" />Del
                                    </button>
                                </div>
                            </div>
                            {resendMsg[e.employee_id] && (
                                <div className="mt-1 text-right text-xs font-semibold text-brand-navy" data-testid={`resend-msg-${e.employee_id}`}>{resendMsg[e.employee_id]}</div>
                            )}
                            {e.role === "worker" && (
                                <div className="mt-2 ml-1">
                                    <button onClick={() => { setAssignFor(assignFor === e.employee_id ? null : e.employee_id); if (!assigns[e.employee_id]) loadAssigns(e.employee_id); }}
                                        className="text-xs text-brand-navy font-semibold" data-testid={`toggle-assign-${e.employee_id}`}>
                                        {assignFor === e.employee_id ? "Hide projects" : "Assign projects"}
                                    </button>
                                    {assignFor === e.employee_id && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {projects.map((p) => {
                                                const on = (assigns[e.employee_id] || []).includes(p.project_id);
                                                return (
                                                    <button key={p.project_id} onClick={() => toggleAssignment(e.employee_id, p.project_id, on)}
                                                        data-testid={`assign-${e.employee_id}-${p.project_id}`}
                                                        className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-brand-navy text-brand-bone border-brand-navy" : "bg-white text-brand-charcoal border-brand-charcoal/20"}`}>
                                                        {p.name} · {p.job_number}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </Card>
        </div>
    );
}

function Projects() {
    const [rows, setRows] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [adding, setAdding] = useState(false);
    // kind: "job" (real, billable) | "pto" (the one PTO bucket) | "task"
    // (non-billable general work -- errands, cleanup, maintenance). Only
    // "job" gets a foreman and phases; the other two are hours-only.
    const [draft, setDraft] = useState({ name: "", job_number: "", manager_id: "", kind: "job" });
    const [editingProjId, setEditingProjId] = useState(null);
    const [editMgrId, setEditMgrId] = useState("");
    const [editName, setEditName] = useState("");
    const [editJob, setEditJob] = useState("");
    const [auditProjId, setAuditProjId] = useState(null);
    const [auditRows, setAuditRows] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [warningDialog, setWarningDialog] = useState(null);
    const [err, setErr] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const load = async () => {
        setErr(null);
        try {
            const [p, e] = await Promise.all([api.get("/projects"), api.get("/employees")]);
            setRows(p.data);
            setEmployees(e.data.filter((x) => x.role === "manager" && x.active));
            setLoaded(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load projects.");
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    /* Per-project job codes used to live here: each project carried its own
       `subtasks` picklist, and admin maintained them job by job. The phase
       catalog replaced that -- one numbered list of phases, the same on
       every job, matching how the estimates are already written. Managed
       from its own screen now (the "Manage phases" link below), not here --
       there is nothing per-project left to set. */
    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0)),
        [rows]
    );
    const submit = async () => {
        try {
            const notBillable = draft.kind !== "job";
            await api.post("/projects", {
                name: draft.name,
                job_number: draft.job_number,
                manager_id: notBillable ? null : (draft.manager_id || null),
                pto_kind: draft.kind === "pto" ? "pto" : null,
                task_kind: draft.kind === "task" ? "task" : null,
            });
            setAdding(false);
            setDraft({ name: "", job_number: "", manager_id: "", kind: "job" });
            await load();
        } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    };
    const toggleActive = async (p) => { await api.patch(`/projects/${p.project_id}`, { active: !p.active }); await load(); };
    const startEdit = (p) => {
        setEditingProjId(p.project_id);
        setEditMgrId(p.manager_id || "");
        setEditName(p.name);
        setEditJob(p.job_number);
    };
    const openAudit = async (p) => {
        setAuditProjId(p.project_id);
        setAuditLoading(true);
        try {
            const r = await api.get(`/projects/${p.project_id}/audit`);
            setAuditRows(r.data);
        } catch (e) {
            setAuditRows([]);
        } finally {
            setAuditLoading(false);
        }
    };
    const applyPatch = async (p, patch) => {
        await api.patch(`/projects/${p.project_id}`, patch);
        setEditingProjId(null);
        await load();
        if (auditProjId === p.project_id) openAudit(p);
    };
    const saveEdit = async (p) => {
        try {
            const patch = {};
            if (editName !== p.name) patch.name = editName;
            if (editJob !== p.job_number) patch.job_number = editJob;
            if ((editMgrId || null) !== (p.manager_id || null)) patch.manager_id = editMgrId || null;
            if (Object.keys(patch).length === 0) { setEditingProjId(null); return; }
            if ("manager_id" in patch) {
                const wk = mondayOf(todayPT());
                const impactResp = await api.get(`/projects/${p.project_id}/impact?week=${wk}`);
                const impact = impactResp.data;
                if ((impact.affected_crew?.length || 0) > 0 || (impact.open_signoffs || 0) > 0) {
                    setWarningDialog({ project: p, patch, impact });
                    return;
                }
            }
            await applyPatch(p, patch);
        } catch (e) {
            alert(e?.response?.data?.detail || "Update failed");
        }
    };

    const deleteProject = async (p) => {
        if (!window.confirm(
            `⚠️ PERMANENTLY DELETE "${p.name}"?\n\nThis will delete ALL time entries, assignments, and sign-off records for this project. This action CANNOT be undone.\n\nType OK to confirm.`
        )) return;
        try {
            await api.delete(`/projects/${p.project_id}`);
            await load();
        } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
    };

    const managerName = (mid) => employees.find((m) => m.employee_id === mid)?.name || (mid ? "(removed)" : "Unassigned");
    const formatChange = (changes, resolvedNames) => {
        if (!changes) return null;
        return Object.entries(changes).map(([key, v]) => {
            let from = v.from, to = v.to;
            if (key === "manager_id") {
                from = resolvedNames?.[from] || from || "Unassigned";
                to = resolvedNames?.[to] || to || "Unassigned";
            } else if (key === "active") {
                from = from ? "Active" : "Archived";
                to = to ? "Active" : "Archived";
            }
            return `${key}: ${from} → ${to}`;
        }).join(" · ");
    };

    if (err && !loaded) return <LoadError message={err} onRetry={load} testId="admin-projects-error" />;

    return (
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="admin-projects">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="font-bold text-2xl">Projects</h2>
                    <p className="text-xs text-brand-charcoal/60">Each project is named by the client. Assign a foreman to manage approvals.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/admin/reports/hours-by-project" className="h-10 px-3 rounded-xl bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold flex items-center gap-2 text-sm" data-testid="hours-report-link">
                        <BarChart3 className="h-4 w-4" />Hours Report
                    </Link>
                    <Link to="/admin/phases" className="h-10 px-3 rounded-xl bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold flex items-center gap-2 text-sm" data-testid="manage-phases-link">
                        <ListOrdered className="h-4 w-4" />Manage Phases
                    </Link>
                    <button onClick={() => setAdding(true)} className="h-10 px-3 rounded-xl bg-brand-navy text-brand-bone font-bold flex items-center gap-2" data-testid="add-project-button"><Plus className="h-4 w-4" />Add project</button>
                </div>
            </div>
            {adding && (
                <Card testId="add-project-form">
                    <div className="flex flex-wrap gap-3 mb-3" data-testid="new-proj-kind">
                        {[
                            ["job", "Real job", "A billable client job — gets phases and a foreman."],
                            ["task", "Non-project task", "Company time not billed to a job — supply runs, shop or yard work, equipment maintenance. Not for personal time; a doctor's appointment or a personal errand is PTO, not this. No phases, no foreman. Create as many of these as you need."],
                            ["pto", "Paid Time Off", "Personal time — appointments, errands, anything off the clock for Talon. Earned, not unlimited: balance is checked before payroll. No phases, no foreman. Only ever create one of these."],
                        ].map(([kind, label, help]) => (
                            <label key={kind}
                                   className={`flex-1 min-w-[160px] rounded-xl border-[1.5px] p-3 text-sm cursor-pointer
                                       ${draft.kind === kind ? "border-brand-navy bg-brand-navy/5" : "border-brand-charcoal/20"}`}
                                   data-testid={`new-proj-kind-${kind}`}>
                                <div className="flex items-center gap-2 font-bold text-brand-charcoal">
                                    <input type="radio" name="proj-kind" checked={draft.kind === kind}
                                           onChange={() => setDraft({ ...draft, kind, manager_id: "" })} />
                                    {label}
                                </div>
                                <p className="text-xs text-brand-charcoal/60 mt-1">{help}</p>
                            </label>
                        ))}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <input placeholder={draft.kind === "job" ? "Client last name (e.g., Whitaker)" : draft.kind === "pto" ? "e.g., Paid Time Off" : "e.g., Errands, Cleanup, Shop Time"} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-proj-name" />
                        <input placeholder={draft.kind === "job" ? "Job number (e.g., JOB-2026-003)" : draft.kind === "pto" ? "e.g., PTO" : "e.g., TASK-ERRANDS"} value={draft.job_number} onChange={(e) => setDraft({ ...draft, job_number: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-proj-number" />
                        {draft.kind === "job" && (
                            <select value={draft.manager_id} onChange={(e) => setDraft({ ...draft, manager_id: e.target.value })} className="h-12 border border-brand-charcoal/20 rounded-xl px-3" data-testid="new-proj-manager">
                                <option value="">— No foreman assigned —</option>
                                {employees.map((m) => <option key={m.employee_id} value={m.employee_id}>{m.name}</option>)}
                            </select>
                        )}
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button onClick={submit} className="h-11 px-4 rounded-xl bg-brand-navy text-brand-bone font-bold" data-testid="save-new-proj">Save</button>
                        <button onClick={() => setAdding(false)} className="h-11 px-4 rounded-xl bg-brand-stone">Cancel</button>
                    </div>
                </Card>
            )}
            <Card testId="projects-list">
                <ul className="divide-y divide-brand-charcoal/10">
                    {[...rows].sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0)).map((p) => (
                        <li key={p.project_id} className="py-3" data-testid={`proj-${p.project_id}`}>
                            {editingProjId === p.project_id ? (
                                <div className="space-y-2" data-testid={`edit-proj-${p.project_id}`}>
                                    <div className="grid sm:grid-cols-3 gap-2">
                                        <input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3" placeholder="Client" data-testid={`edit-name-${p.project_id}`} />
                                        <input value={editJob} onChange={(e) => setEditJob(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3" placeholder="Job number" data-testid={`edit-job-${p.project_id}`} />
                                        <select value={editMgrId} onChange={(e) => setEditMgrId(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3" data-testid={`edit-manager-${p.project_id}`}>
                                            <option value="">— No foreman assigned —</option>
                                            {employees.map((m) => <option key={m.employee_id} value={m.employee_id}>{m.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => saveEdit(p)} className="h-10 px-4 rounded-lg bg-brand-navy text-brand-bone font-bold text-xs" data-testid={`save-edit-${p.project_id}`}>Save</button>
                                        <button onClick={() => setEditingProjId(null)} className="h-10 px-4 rounded-lg bg-brand-stone font-bold text-xs">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-between items-center gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="font-bold truncate">{p.name}</div>
                                        <div className="text-xs text-brand-charcoal/60">{p.job_number} · Foreman: <span className="font-semibold text-brand-charcoal">{managerName(p.manager_id)}</span></div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {!p.pto_kind && !p.task_kind && (
                                            <button onClick={() => openAudit(p)} className="h-8 px-3 rounded-lg bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold text-xs uppercase tracking-widest flex items-center gap-1" data-testid={`history-proj-button-${p.project_id}`} title="View change history">
                                                <History className="h-3.5 w-3.5" />History
                                            </button>
                                        )}
                                        {!p.pto_kind && !p.task_kind && (
                                            <button onClick={() => startEdit(p)} className="h-8 px-3 rounded-lg bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold text-xs uppercase tracking-widest" data-testid={`edit-proj-button-${p.project_id}`}>Edit</button>
                                        )}
                                        <button onClick={() => toggleActive(p)} className={`text-xs px-2 py-1 rounded-lg ${p.active ? "bg-brand-success/10 text-brand-success" : "bg-brand-danger/10 text-brand-danger"}`} data-testid={`toggle-proj-${p.project_id}`}>
                                            {p.active ? "Active" : "Archived"}
                                        </button>
                                        {!p.pto_kind && !p.task_kind && (
                                            <button onClick={() => deleteProject(p)} className="h-8 px-3 rounded-lg bg-brand-danger/10 hover:bg-brand-danger/10 text-brand-danger font-bold text-xs uppercase tracking-widest flex items-center gap-1" data-testid={`delete-proj-${p.project_id}`} title="Permanently delete project and all its data">
                                                <X className="h-3.5 w-3.5" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {auditProjId === p.project_id && (
                                <div className="mt-3 bg-brand-stone/50 border border-brand-charcoal/10 rounded-xl p-3" data-testid={`audit-panel-${p.project_id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] uppercase tracking-widest text-brand-navy font-bold">Change history</span>
                                        <button onClick={() => { setAuditProjId(null); setAuditRows([]); }} className="p-1 rounded-lg hover:bg-white text-brand-charcoal/60" aria-label="Close audit" data-testid={`close-audit-${p.project_id}`}>
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    {auditLoading && (
                                        <div className="text-xs text-brand-charcoal/60 py-2">Loading…</div>
                                    )}
                                    {!auditLoading && auditRows.length === 0 && (
                                        <div className="text-xs text-brand-charcoal/60 py-2 italic">No changes recorded yet.</div>
                                    )}
                                    {!auditLoading && auditRows.length > 0 && (
                                        <ul className="space-y-2 text-xs" data-testid={`audit-rows-${p.project_id}`}>
                                            {auditRows.map((r) => (
                                                <li key={r.audit_id} className="bg-white border border-brand-charcoal/10 rounded-lg p-2" data-testid={`audit-row-${r.audit_id}`}>
                                                    <div className="flex justify-between items-baseline">
                                                        <span className="font-bold text-brand-charcoal">{r.action.replace(/_/g, " ")}</span>
                                                        <span className="text-[10px] text-brand-charcoal/60">{dayjs(r.created_at).format("MMM D, YYYY · h:mm A")}</span>
                                                    </div>
                                                    <div className="text-brand-charcoal/70 mt-0.5">by <span className="font-semibold text-brand-charcoal">{r.by_name}</span></div>
                                                    {r.changes && Object.keys(r.changes).length > 0 && (
                                                        <div className="text-brand-navy mt-1 break-words">{formatChange(r.changes, r.resolved_names)}</div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </Card>

            {warningDialog && (
                <ReassignWarningDialog
                    dialog={warningDialog}
                    managerName={managerName}
                    onCancel={() => setWarningDialog(null)}
                    onConfirm={async () => {
                        const { project, patch } = warningDialog;
                        setWarningDialog(null);
                        try {
                            await applyPatch(project, patch);
                        } catch (e) {
                            alert(e?.response?.data?.detail || "Update failed");
                        }
                    }}
                />
            )}
        </div>
    );
}

// The catalog every job is logged against. Kim's own bar for the entry
// screen: crew choose only project and phase, both admin-managed -- this is
// the "phase" half. Until this screen existed, phase was only half true:
// project has had a real admin screen all along, but the phase list was a
// hardcoded file only a code change could touch. See routes/phases.py.
function Phases() {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [err, setErr] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [newCrewVisible, setNewCrewVisible] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingNumber, setEditingNumber] = useState(null);
    const [editName, setEditName] = useState("");

    const load = async () => {
        setErr(null);
        try {
            const r = await api.get("/phases/all");
            setRows(r.data);
            setLoaded(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load phases.");
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const createPhase = async () => {
        const name = newName.trim();
        if (!name) { toast.error("Give the phase a name."); return; }
        setSaving(true);
        try {
            await api.post("/phases", { name, crew_visible: newCrewVisible });
            toast.success("Phase added");
            setNewName("");
            setNewCrewVisible(true);
            setAdding(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn’t add the phase.");
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (p) => { setEditingNumber(p.phase_number); setEditName(p.name); };

    const saveRename = async (p) => {
        const name = editName.trim();
        if (!name) { toast.error("A phase needs a name."); return; }
        if (name === p.name) { setEditingNumber(null); return; }
        try {
            await api.patch(`/phases/${p.phase_number}`, { name });
            toast.success("Renamed");
            setEditingNumber(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn’t rename.");
        }
    };

    const toggleCrewVisible = async (p) => {
        try {
            await api.patch(`/phases/${p.phase_number}`, { crew_visible: !p.crew_visible });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn’t update.");
        }
    };

    // The 400 this can come back with (MATERIAL, see routes/phases.py) is
    // shown as-is rather than pre-empted here -- `protected` below already
    // keeps that control out of reach for the one row it applies to, so this
    // path is only ever hit by an ordinary phase.
    const toggleActive = async (p) => {
        try {
            await api.patch(`/phases/${p.phase_number}`, { active: !p.active });
            toast.success(p.active ? "Deactivated" : "Reactivated");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn’t update.");
        }
    };

    if (err && !loaded) return <LoadError message={err} onRetry={load} testId="admin-phases-error" />;

    return (
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="admin-phases">
            <div className="flex justify-between items-center gap-3">
                <div>
                    <h2 className="font-bold text-2xl">Phases</h2>
                    <p className="text-xs text-brand-charcoal/60">
                        The numbered list every job is logged against. The number never changes once a
                        phase exists — payroll and Talon’s estimates join on it, not the name.
                    </p>
                </div>
                <button onClick={() => setAdding((v) => !v)}
                        className="h-10 px-3 rounded-xl bg-brand-navy text-brand-bone font-bold flex items-center gap-2 shrink-0"
                        data-testid="add-phase-button">
                    <Plus className="h-4 w-4" />Add phase
                </button>
            </div>

            {adding && (
                <Card testId="add-phase-form">
                    <div className="space-y-3">
                        <input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Phase name, e.g. Landscaping"
                            data-testid="new-phase-name"
                            className="w-full h-11 border border-brand-charcoal/20 rounded-xl px-3"
                        />
                        <label className="flex items-center gap-2 text-sm text-brand-charcoal/70">
                            <input type="checkbox" checked={newCrewVisible}
                                   onChange={(e) => setNewCrewVisible(e.target.checked)}
                                   data-testid="new-phase-crew-visible" />
                            Crew can select this phase
                        </label>
                        <div className="flex gap-2">
                            <button onClick={createPhase} disabled={saving}
                                    className="h-10 px-4 rounded-xl bg-brand-navy text-brand-bone font-bold disabled:opacity-50"
                                    data-testid="save-new-phase">
                                {saving ? "Adding…" : "Add"}
                            </button>
                            <button onClick={() => { setAdding(false); setNewName(""); }}
                                    className="h-10 px-4 rounded-xl border border-brand-charcoal/20 font-bold text-brand-charcoal/70">
                                Cancel
                            </button>
                        </div>
                    </div>
                </Card>
            )}

            <Card>
                <div className="divide-y divide-brand-charcoal/10">
                    {rows.map((p) => (
                        <div key={p.phase_number}
                             className={`py-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 ${!p.active ? "opacity-50" : ""}`}
                             data-testid={`phase-row-${p.phase_number}`}>
                            <span className="text-xs font-bold tabular-nums text-brand-charcoal/45 w-6 text-right shrink-0">
                                {p.phase_number}
                            </span>

                            {editingNumber === p.phase_number ? (
                                <input
                                    autoFocus
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") saveRename(p);
                                        if (e.key === "Escape") setEditingNumber(null);
                                    }}
                                    data-testid={`phase-edit-name-${p.phase_number}`}
                                    className="flex-1 min-w-[140px] h-9 border border-brand-charcoal/20 rounded-lg px-2"
                                />
                            ) : (
                                <span className="flex-1 min-w-[140px] font-semibold text-brand-charcoal">{p.name}</span>
                            )}

                            {!p.active && (
                                <span className="text-[10px] uppercase tracking-widest text-brand-charcoal/50 bg-brand-stone rounded-full px-2 py-0.5">
                                    Inactive
                                </span>
                            )}
                            {!p.crew_visible && (
                                <span className="text-[10px] uppercase tracking-widest text-brand-charcoal/50 bg-brand-stone rounded-full px-2 py-0.5">
                                    Office only
                                </span>
                            )}

                            {editingNumber === p.phase_number ? (
                                <div className="flex items-center gap-3 shrink-0">
                                    <button onClick={() => saveRename(p)} className="text-xs font-bold text-brand-navy"
                                            data-testid={`phase-save-${p.phase_number}`}>Save</button>
                                    <button onClick={() => setEditingNumber(null)} className="text-xs font-bold text-brand-charcoal/50">
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 shrink-0">
                                    <button onClick={() => startEdit(p)}
                                            className="text-xs font-bold text-brand-charcoal/60 hover:text-brand-navy"
                                            data-testid={`phase-rename-${p.phase_number}`}>
                                        Rename
                                    </button>
                                    <button onClick={() => toggleCrewVisible(p)}
                                            className="text-xs font-bold text-brand-charcoal/60 hover:text-brand-navy"
                                            data-testid={`phase-crew-toggle-${p.phase_number}`}>
                                        {p.crew_visible ? "Hide from crew" : "Show to crew"}
                                    </button>
                                    {p.protected ? (
                                        <span
                                            className="text-xs text-brand-charcoal/40"
                                            title="This phase books material cost against a job and can’t be deactivated here."
                                            data-testid={`phase-protected-${p.phase_number}`}
                                        >
                                            Protected
                                        </span>
                                    ) : (
                                        <button onClick={() => toggleActive(p)}
                                                className="text-xs font-bold text-brand-charcoal/60 hover:text-brand-danger"
                                                data-testid={`phase-toggle-active-${p.phase_number}`}>
                                            {p.active ? "Deactivate" : "Reactivate"}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}

function ReassignWarningDialog({ dialog, managerName, onCancel, onConfirm }) {
    const { project, patch, impact } = dialog;
    const newMgrName = patch.manager_id ? managerName(patch.manager_id) : "Unassigned";
    const oldMgrName = managerName(project.manager_id);
    return (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="reassign-warning-dialog">
            <div className="bg-white rounded-2xl shadow-lift max-w-md w-full overflow-hidden">
                <div className="bg-brand-warn/15 border-b border-brand-warn/40 px-5 py-4 flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-brand-charcoal shrink-0 mt-0.5" />
                    <div>
                        <div className="font-bold text-xl text-brand-charcoal">Hold on — open work on this project</div>
                        <div className="text-xs text-brand-charcoal/70 mt-1">Reassigning <b>{project.name}</b> ({project.job_number}) from <b>{oldMgrName}</b> to <b>{newMgrName}</b> for the week of {dayjs(impact.week_start).format("MMM D")}.</div>
                    </div>
                </div>
                <div className="p-5 space-y-3">
                    {impact.affected_crew.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-navy font-bold mb-2">Affected crew this week ({impact.affected_crew.length})</div>
                            <ul className="space-y-1 text-sm" data-testid="warning-affected-list">
                                {impact.affected_crew.map((c) => (
                                    <li key={c.employee_id} className="flex justify-between border-b border-brand-charcoal/10 pb-1 last:border-0" data-testid={`warning-row-${c.employee_id}`}>
                                        <span><span className="font-semibold">{c.employee_name}</span> <span className="text-[10px] text-brand-charcoal/50">{c.employee_code}</span></span>
                                        <span className="text-brand-navy">{c.hours_on_project.toFixed(2)}h · <span className="uppercase text-[10px] tracking-widest">{c.status}</span></span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {impact.open_signoffs > 0 && (
                        <div className="bg-brand-warn/15 border border-brand-warn/40 rounded-xl p-3 text-xs text-brand-charcoal">
                            <b>{impact.open_signoffs}</b> existing per-job sign-off{impact.open_signoffs === 1 ? "" : "s"} on this project for this week stay in place (they're attached to the timecard, not the foreman).
                        </div>
                    )}
                    <div className="text-xs text-brand-charcoal/70 italic">
                        The new foreman will see this week's entries on their summary going forward.
                    </div>
                </div>
                <div className="border-t border-brand-charcoal/10 px-5 py-3 flex gap-2 justify-end">
                    <button onClick={onCancel} className="h-10 px-4 rounded-lg bg-brand-stone text-brand-charcoal font-bold text-sm" data-testid="warning-cancel">Cancel</button>
                    <button onClick={onConfirm} className="h-10 px-4 rounded-lg bg-brand-navy text-brand-bone font-bold text-sm" data-testid="warning-confirm">Reassign anyway</button>
                </div>
            </div>
        </div>
    );
}

function Export() {
    const [year, setYear] = useState(currentYearPT());
    const [pp, setPp] = useState(() => {
        const ys = dayjs(`${currentYearPT()}-01-01`);
        return Math.max(1, Math.floor(dayjs(todayPT()).diff(ys, "week")) + 1);
    });
    const [reports, setReports] = useState([]);
    const [reportsOpen, setReportsOpen] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        api.get("/reports").then((r) => setReports(r.data)).catch(() => {});
    }, []);

    const download = async () => {
        const res = await fetch(`${API}/export?pay_period=${pp}&year=${year}`, { credentials: "include" });
        if (!res.ok) {
            const text = await res.text();
            alert(text || "Export failed — make sure at least one timecard is in Approved status for this week.");
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `talon_payroll_pp${pp}_${year}.xlsx`; a.click(); URL.revokeObjectURL(url);
        // Refresh report history
        const r = await api.get("/reports").catch(() => ({ data: [] }));
        setReports(r.data);
    };
    return (
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="admin-export">
            <h2 className="font-bold text-2xl">Payroll Export (Excel · .xlsx)</h2>
            <Card>
                <p className="text-sm text-brand-charcoal/80 mb-2">
                    Downloads a multi-tab Excel workbook for the selected pay-period week. The workbook has four sheets:
                </p>
                <ul className="text-sm text-brand-charcoal/80 list-disc ml-5 mb-4 space-y-1">
                    <li><b>By Project</b> — every entry grouped by project, with the phase number, phase name and hours on each row, and per-project subtotals.</li>
                    <li><b>By Crew</b> — the same entries grouped by crew member, with per-employee subtotals.</li>
                    <li><b>Payroll Summary</b> — one row per employee: total hours for the week.</li>
                    <li><b>Meal Periods</b> — one row per day per employee: whether the 30-minute meal was taken, and the reason given when it was not.</li>
                </ul>
                <p className="text-xs text-brand-charcoal/60 mb-4">
                    Only timecards in <b>Approved</b> status are included. Exporting flips them to <b>Exported</b> (permanently locked).
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-brand-navy mb-1">Pay period (1–52)</label>
                        <input type="number" min="1" max="52" value={pp} onChange={(e) => setPp(parseInt(e.target.value) || 1)} className="h-12 w-full border border-brand-charcoal/20 rounded-xl px-3 text-base" data-testid="export-pp" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-brand-navy mb-1">Year</label>
                        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-12 w-full border border-brand-charcoal/20 rounded-xl px-3 text-base" data-testid="export-year" />
                    </div>
                </div>
                <button onClick={download} className="mt-4 w-full h-14 bg-brand-navy hover:bg-brand-navy-dark text-brand-bone rounded-xl font-bold flex items-center justify-center gap-2" data-testid="download-csv-button">
                    <FileDown className="h-5 w-5" /> Download Excel workbook
                </button>
            </Card>
            {/* Report History */}
            <button onClick={() => setReportsOpen(!reportsOpen)} className="w-full flex items-center justify-between bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-4 text-sm font-bold text-brand-charcoal" data-testid="toggle-reports-history">
                <span className="flex items-center gap-2"><History className="h-4 w-4 text-brand-navy" /> Export history ({reports.length} saved reports)</span>
                {reportsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {reportsOpen && (
                <Card testId="report-history">
                    {reports.length === 0 && <p className="text-sm text-brand-charcoal/50">No exports yet.</p>}
                    <ul className="divide-y divide-brand-charcoal/10 text-sm">
                        {reports.map((r) => (
                            <li key={r.report_id} className="py-3" data-testid={`report-${r.report_id}`}>
                                <div className="flex justify-between items-baseline flex-wrap gap-2">
                                    <div>
                                        <span className="font-bold">{formatRange(r.week_start)}</span>
                                        <span className="text-brand-charcoal/50 text-xs ml-2">Pay Period {r.pay_period} · {r.year}</span>
                                    </div>
                                    <div className="text-xs text-brand-charcoal/50">
                                        {dayjs(r.exported_at).format("MMM D, YYYY h:mm A")} · by {r.exported_by_name}
                                    </div>
                                </div>
                                <div className="flex gap-4 mt-1.5 text-xs flex-wrap">
                                    <span className="text-brand-success font-bold">{r.employee_count} employees</span>
                                    <span>Paid: <b>{r.total_paid_hours}h</b></span>
                                    <span>Sick: <b>{r.total_sick}h</b></span>
                                    <span>Vacation: <b>{r.total_vacation}h</b></span>
                                    <span>Total: <b>{r.total_hours}h</b></span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}
        </div>
    );
}

function Checklist() {
    const [d, setD] = useState(null);
    const [week, setWeek] = useState(mondayOf(todayPT()));
    const [bulkWorking, setBulkWorking] = useState(false);

    const [err, setErr] = useState(null);
    const load = async (w) => {
        setErr(null);
        try {
            const r = await api.get(`/dashboard/paycheck-prep?week=${w}`);
            setD(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load the payroll checklist.");
        }
    };
    useEffect(() => { load(week); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps

    const bulkApprove = async () => {
        if (!window.confirm(`Approve all ${d.counts.submitted} submitted timecards for ${formatRange(d.week_start)}?`)) return;
        setBulkWorking(true);
        for (const tc of d.awaiting_signoff) {
            try { await api.post(`/timecards/${tc.timecard_id}/approve`); }
            catch (e) { console.warn(`Bulk approve failed for ${tc.timecard_id}:`, e?.response?.data?.detail || e?.message); }
        }
        await load(week);
        setBulkWorking(false);
    };

    if (err && !d) return <LoadError message={err} onRetry={() => load(week)} testId="admin-checklist-error" />;
    if (!d) return <div className="p-6 text-brand-charcoal/60">Loading…</div>;

    const c = d.counts;
    const statTiles = [
        { label: "Missing", val: c.missing, icon: AlertCircle, color: "text-brand-danger", bg: "bg-brand-danger/10" },
        { label: "Submitted", val: c.submitted, icon: Clock, color: "text-brand-charcoal", bg: "bg-brand-warn/15" },
        { label: "Approved", val: c.approved, icon: CheckCircle2, color: "text-brand-success", bg: "bg-brand-success/10" },
        { label: "Exported", val: c.exported, icon: PackageCheck, color: "text-brand-steel", bg: "bg-brand-stone/50" },
    ];

    return (
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="paycheck-prep">
            <div className="bg-brand-admin-header text-brand-bone rounded-2xl p-5 shadow-lift flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Pay-Period Checklist · Pay Period {d.pay_period} of 52 · {d.year}</div>
                    <div className="font-bold text-3xl mt-1">{formatRange(d.week_start)}</div>
                    <div className="text-xs text-brand-bone/70 mt-1">Crew submit by Sun 11:59 PM · Foreman approve by Mon 11:59 PM · Payday Fri</div>
                </div>
                <input type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))} className="text-xs text-brand-charcoal rounded-lg px-2 py-1" data-testid="checklist-week-picker" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {statTiles.map((t) => (
                    <Card key={t.label} testId={`tile-${t.label.toLowerCase()}`} className={t.bg}>
                        <div className="flex items-center gap-2 mb-1">
                            <t.icon className={`h-4 w-4 ${t.color}`} />
                            <div className={`text-xs uppercase tracking-widest font-bold ${t.color}`}>{t.label}</div>
                        </div>
                        <div className={`font-bold text-4xl ${t.color}`}>{t.val}</div>
                        <div className="text-xs text-brand-charcoal/50 mt-1">of {c.total_crew} crew</div>
                    </Card>
                ))}
            </div>

            {d.awaiting_signoff.length > 0 && (
                <Card testId="awaiting-signoff">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="text-[10px] uppercase tracking-widest text-brand-charcoal font-bold">Awaiting sign-off ({d.awaiting_signoff.length})</div>
                        {d.counts.submitted > 0 && (
                            <button onClick={bulkApprove} disabled={bulkWorking} className="h-9 px-4 rounded-xl bg-brand-success hover:bg-brand-success/85 text-white font-bold text-xs uppercase tracking-widest disabled:opacity-50" data-testid="bulk-approve-button">
                                {bulkWorking ? "Approving…" : "Bulk approve all submitted"}
                            </button>
                        )}
                    </div>
                    <ul className="divide-y divide-brand-charcoal/10 text-sm">
                        {d.awaiting_signoff.map((emp) => (
                            <li key={emp.employee_id} className="py-2.5" data-testid={`awaiting-${emp.employee_id}`}>
                                <div className="font-bold">{emp.name}</div>
                                {emp.unsigned_projects.length > 0 && (
                                    <div className="text-xs text-brand-charcoal mt-0.5">
                                        Unsigned: {emp.unsigned_projects.map((p) => p.name).join(", ")}
                                    </div>
                                )}
                                {emp.signed_projects.length > 0 && (
                                    <div className="text-xs text-brand-success mt-0.5">
                                        Signed: {emp.signed_projects.map((p) => p.name).join(", ")}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {d.ready_to_export.length > 0 && (
                <Card testId="ready-to-export">
                    <div className="text-[10px] uppercase tracking-widest text-brand-success font-bold mb-3">Ready to export ({d.ready_to_export.length})</div>
                    <ul className="divide-y divide-brand-charcoal/10 text-sm">
                        {d.ready_to_export.map((emp) => (
                            <li key={emp.employee_id} className="flex justify-between py-2" data-testid={`ready-${emp.employee_id}`}>
                                <span>{emp.name}</span>
                                <span className="font-bold text-brand-success">{emp.hours.toFixed(2)} hrs</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-xs text-brand-charcoal/50 mt-3">Go to the Export tab to download the Excel workbook.</p>
                </Card>
            )}

            {d.missing_employees.length > 0 && (
                <Card testId="missing-employees">
                    <div className="text-[10px] uppercase tracking-widest text-brand-danger font-bold mb-2">Not yet submitted ({d.missing_employees.length})</div>
                    <ul className="text-sm divide-y divide-brand-charcoal/10">
                        {d.missing_employees.map((emp) => (
                            <li key={emp.employee_id} className="py-2 text-brand-charcoal/80" data-testid={`missing-${emp.employee_id}`}>{emp.name}</li>
                        ))}
                    </ul>
                </Card>
            )}
        </div>
    );
}

export default function AdminApp() {
    return (
        <div className="min-h-screen paper-bg flex flex-col">
            <AppHeader />
            <main className="flex-1">
                <Routes>
                    <Route index element={<Dashboard />} />
                    <Route path="checklist" element={<Checklist />} />
                    <Route path="employees" element={<Employees />} />
                    <Route path="projects" element={<Projects />} />
                    <Route path="phases" element={<Phases />} />
                    <Route path="reports/hours-by-project" element={<HoursByProjectReport />} />
                    <Route path="export" element={<Export />} />
                    <Route path="safety" element={<SafetyMeetings canManage canViewReport />} />
                    <Route path="safety/compliance-report" element={<SafetyComplianceRollup />} />
                    <Route path="safety/:meetingId/report" element={<SafetyMeetingReport />} />
                    <Route path="announcements" element={<AnnouncementsPage />} />
                </Routes>
            </main>
            <BottomTabs
                tabs={[
                    { to: "/admin", label: "Dash", icon: LayoutDashboard, end: true },
                    { to: "/admin/checklist", label: "Checklist", icon: ClipboardCheck },
                    { to: "/admin/employees", label: "People", icon: Users },
                    { to: "/admin/projects", label: "Projects", icon: FolderKanban },
                    { to: "/admin/safety", label: "Safety", icon: ShieldCheck },
                    { to: "/admin/export", label: "Export", icon: FileDown },
                    { to: "/admin/announcements", label: "News", icon: Megaphone },
                ]}
            />
        </div>
    );
}
